import React from 'react';
import PropTypes from 'prop-types';

import RIQDropdownTitle from './RIQDropdownTitle.jsx';

import {
    Menu
} from '@headlessui/react';

import {
    useIsExporting
} from '../hooks/useExport.js';

import {
    useRIQTooltip
} from './RIQPopover.jsx';

import useTrackingId from '../hooks/useTrackingId.js';

function DropdownItem({dropdownOption, onClick}) {

    function dropdownItemClassName(active, dropdownOption) {

        return [
            dropdownOption.disabled ? 'tlw-text-gray-500 hover_!tlw-text-gray-500 tlw-bg-transparent tlw-cursor-not-allowed' :
                (dropdownOption.selected ?
                    'tlw-bg-gray-800 tlw-text-white tlw-rounded-sm' :
                    (active ? 'tlw-bg-blue-400 tlw-text-white tlw-rounded-sm' : 'tlw-text-gray-900 tlw-bg-transparent hover_tlw-bg-blue-400')),
            'tlw-w-full tlw-border-none tlw-text-left tlw-text-md tlw-px-5 tlw-py-1.5 tlw-block hover_tlw-text-white hover_tlw-no-underline'
        ].join(' ');
    }
    return (
        <>
            {dropdownOption.isDivider && <div className='tlw-mx-[-2px] tlw-my-1 tlw-h-0.5 tlw-bg-gray-300'/>}

            {!dropdownOption.isDivider &&
            <Menu.Item
                key={dropdownOption.id}
                disabled={dropdownOption.disabled}>
                {({active}) => {
                    // Because of how the headlessui Menu component works, we need to return a button or an anchor tag as the top level element.
                    // I.e., we can't return a fragment (<>...</>); if we do, the `active` prop won't be updated properly.
                    if (dropdownOption.href) {
                        return (
                            <a
                                href={dropdownOption.href}
                                className={dropdownItemClassName(active, dropdownOption)}
                            >
                                {dropdownOption.name}
                            </a>
                        );
                    } else {
                        return (
                            <button
                                className={dropdownItemClassName(active, dropdownOption)}
                                onClick={onClick}
                            >
                                {dropdownOption.name}
                            </button>
                        );
                    }
                }}
            </Menu.Item>
            }
        </>
    );
}

RIQDropdown.propTypes = {
    variant: PropTypes.oneOf(['large', 'small', 'toolbar']).isRequired,
    title: PropTypes.node.isRequired,
    showSelectedInTitle: PropTypes.bool,
    dropdownOptions: PropTypes.array.isRequired,
    onItemSelected: PropTypes.func.isRequired
};

RIQDropdown.DIVIDER = '__DIVIDER__';

export default function RIQDropdown({variant, title, showSelectedInTitle, dropdownOptions, onItemSelected, isDisabled, activeFilter = false, tooltipText = null, trackingId}) {

    const {setTriggerRef, tooltipNode} = useRIQTooltip({
        text: tooltipText,
        placement: 'bottom',
        disabled: !isDisabled
    });

    const isExporting = useIsExporting();

    let dividerCount = 0;
    const _dropdownOptions = _.map(dropdownOptions, dropdownOption => {
        if (dropdownOption === RIQDropdown.DIVIDER) {
            return {
                id: `${RIQDropdown.DIVIDER}_${dividerCount++}`,
                isDivider: true
            };
        } else {
            return dropdownOption;
        }
    });

    let variantClasses;
    switch (variant) {

        case 'large':
            variantClasses = 'tlw-px-6 tlw-py-3 tlw-text-md tlw-font-normal';
            break;

        case 'small':
            variantClasses = 'tlw-px-2.5 tlw-py-0.5 tlw-text-sm tlw-font-normal';
            break;
        case 'toolbar':
            variantClasses = 'tlw-px-6 tlw-py-1.5 tlw-h-17 tlw-justify-between';
            break;

        default:
            throw new Error(`Unknown button variant: ${variant}`);
    }

    if (showSelectedInTitle) {
        const selectedOption = _.find(_dropdownOptions, dropdownOption => dropdownOption.selected);
        if (selectedOption) {
            title = <RIQDropdownTitle
                label={title}
                selectionSummary={selectedOption.name}
            />;
        }
    }

    const trackingRef = useTrackingId(trackingId);

    return (
        <>
            {!isExporting &&
                <Menu
                    as='div'
                    className='tlw-relative tlw-inline-block tlw-text-left'
                    ref={(el) => {
                        setTriggerRef(el);
                        trackingRef.current = el;
                    }}
                >
                    {({open}) => (
                        <>
                            <Menu.Button
                                className={
                                    [
                                        open ? 'tlw-border-[#d4d4d4] tlw-bg-[#e6e6e6] tlw-text-gray-800' : 'tlw-border-gray-600 tlw-bg-white tlw-text-gray-800 hover_tlw-bg-gray-100',
                                        `tlw-flex tlw-w-full tlw-items-center tlw-rounded-sm tlw-border tlw-border-solid tlw-shadow-sm ${variantClasses}`,
                                        activeFilter ? `tlw-border-l-5 tlw-border-l-gray-800` : '',
                                        isDisabled ? `tlw-border-gray-200 tlw-shadow-none tlw-opacity-65` : ''
                                    ].join(' ')}
                                disabled={isDisabled}
                            >
                                {title}
                                <span className='fa fa-angle-down tlw-ml-1.5'/>
                            </Menu.Button>

                            <Menu.Items className='tlw-absolute tlw-right-0 tlw-z-10 tlw-mt-1 tlw-w-full tlw-min-w-80 tlw-origin-top-right tlw-whitespace-nowrap tlw-rounded tlw-border tlw-border-solid tlw-border-black/[0.15] tlw-bg-white tlw-p-1 tlw-shadow-dropdown focus_tlw-outline-none'>

                                <div className='tlw-max-h-[288px] tlw-overflow-y-auto tlw-px-1'>

                                    {_.map(_dropdownOptions, dropdownOption => (
                                        <DropdownItem
                                            key={dropdownOption.id}
                                            dropdownOption={dropdownOption}
                                            onClick={() => onItemSelected(dropdownOption)}
                                        />
                                    ))}

                                </div>

                            </Menu.Items>
                        </>
                    )}
                </Menu>
            }
            {tooltipNode}
        </>
    );
}
